import { FC } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors, spacing } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

interface FetchResult {
  url: string;
  content?: string;
  error?: string;
  status?: string;
  summarized?: boolean;
  type?: string;
  prompt?: string;
  original_length?: number;
  total_length?: number;
  returned_length?: number;
  has_more?: boolean;
  next_start_index?: number;
}

const markdownStyles = css`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: ${spacing.deci} 0 ${spacing.centi} 0;
    color: ${colors.liquorice};
    font-weight: 600;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  }

  h1 {
    font-size: 18px;
  }
  h2 {
    font-size: 16px;
  }
  h3 {
    font-size: 15px;
  }
  h4,
  h5,
  h6 {
    font-size: 14px;
  }

  p {
    margin: ${spacing.centi} 0;
  }

  ul,
  ol {
    margin: ${spacing.centi} 0;
    padding-left: ${spacing.regular};
  }

  li {
    margin: ${spacing.micro} 0;
  }

  blockquote {
    border-left: 3px solid ${colors.petrolBlue};
    margin: ${spacing.centi} 0;
    padding-left: ${spacing.deci};
    font-style: italic;
    color: ${colors.silver};

    html[data-theme='dark'] & {
      border-left-color: ${colors.skyBlue};
      color: ${colors.cloud};
    }
  }

  code {
    background-color: ${colors.fog};
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'SF Mono', Monaco, monospace;
    font-size: 13px;

    html[data-theme='dark'] & {
      background-color: var(--card-background);
      color: ${colors.sugar};
    }
  }

  pre {
    background-color: ${colors.fog};
    padding: ${spacing.deci};
    border-radius: 6px;
    overflow-x: auto;
    margin: ${spacing.centi} 0;

    html[data-theme='dark'] & {
      background-color: var(--card-background);
    }

    code {
      background-color: transparent;
      padding: 0;
    }
  }

  a {
    color: ${colors.petrolBlue};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: ${spacing.centi} 0;
  }

  th,
  td {
    border: 1px solid ${colors.cloud};
    padding: 8px;
    text-align: left;

    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};
    }
  }

  th {
    background-color: ${colors.fog};
    font-weight: 600;

    html[data-theme='dark'] & {
      background-color: var(--card-background);
    }
  }
`;

const SingleFetchResult: FC<{ result: FetchResult; showDivider?: boolean }> = ({ result, showDivider }) => {
  const url = result.url;
  const content = result.content;
  const isAISummarized = result.summarized === true || result.type === 'web_content_summarized';
  const prompt = result.prompt;

  // Handle error cases
  if (result.error || result.status === 'error') {
    return (
      <div
        css={css`
          ${showDivider &&
          `
            margin-top: ${spacing.deci};
            padding-top: ${spacing.deci};
            border-top: 1px solid ${colors.cloud};
            html[data-theme='dark'] & {
              border-top-color: ${colors.tungsten};
            }
          `}
        `}
      >
        <div
          css={css`
            font-size: 14px;
            color: ${colors.berry};
            padding: ${spacing.centi};
            background-color: rgba(208, 63, 23, 0.05);
            border-radius: 6px;
            border: 1px solid rgba(208, 63, 23, 0.15);
            margin: ${spacing.centi} 0;

            html[data-theme='dark'] & {
              background-color: rgba(208, 63, 23, 0.1);
              border-color: rgba(208, 63, 23, 0.2);
              color: ${colors.berry};
            }
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            Failed to fetch content from {url}
          </div>
          <div>{result.error}</div>
        </div>
      </div>
    );
  }

  if (!content && !url) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
          ${showDivider &&
          `
            margin-top: ${spacing.deci};
            padding-top: ${spacing.deci};
            border-top: 1px solid ${colors.cloud};
            html[data-theme='dark'] & {
              border-top-color: ${colors.tungsten};
            }
          `}
        `}
      >
        Web content fetched
      </div>
    );
  }

  const cleanedContent = content ? cleanMarkdownContent(content) : '';

  return (
    <div
      css={css`
        ${showDivider &&
        `
          margin-top: ${spacing.deci};
          padding-top: ${spacing.deci};
          border-top: 1px solid ${colors.cloud};
          html[data-theme='dark'] & {
            border-top-color: ${colors.tungsten};
          }
        `}
      `}
    >
      {/* Header with URL and metadata */}
      <div
        css={css`
          font-size: 12px;
          color: ${colors.silver};
          margin-bottom: ${spacing.centi};
          padding-bottom: ${spacing.centi};
          border-bottom: 1px solid ${colors.cloud};

          html[data-theme='dark'] & {
            border-bottom-color: ${colors.tungsten};
          }
        `}
      >
        {url && (
          <div
            css={css`
              margin-bottom: ${spacing.milli};
            `}
          >
            <span>Content from: </span>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              css={css`
                color: ${colors.petrolBlue};
                text-decoration: none;
                word-break: break-all;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {url}
            </a>
          </div>
        )}

        {isAISummarized && prompt && (
          <div
            css={css`
              margin-bottom: ${spacing.milli};
              font-style: italic;
            `}
          >
            <span
              css={css`
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                background-clip: text;
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                font-weight: 600;
              `}
            >
              AI Summary
            </span>{' '}
            based on: &ldquo;{prompt}&rdquo;
          </div>
        )}

        {result.original_length && (
          <div>
            {isAISummarized
              ? `Summarized from ${result.original_length.toLocaleString()} characters`
              : result.has_more
                ? `Showing ${result.returned_length?.toLocaleString() || 0} of ${result.total_length?.toLocaleString() || 0} characters`
                : `${result.total_length?.toLocaleString() || 0} characters`}
          </div>
        )}
      </div>

      {/* Main content */}
      <div
        css={css`
          font-size: 14px;
          line-height: 1.6;
          color: ${colors.liquorice};

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} css={markdownStyles}>
          {cleanedContent}
        </ReactMarkdown>
      </div>

      {/* Pagination info for non-summarized content */}
      {!isAISummarized && result.has_more && result.next_start_index && (
        <div
          css={css`
            margin-top: ${spacing.deci};
            padding: ${spacing.centi};
            background-color: ${colors.fog};
            border-radius: 6px;
            font-size: 12px;
            color: ${colors.silver};

            html[data-theme='dark'] & {
              background-color: var(--card-background);
            }
          `}
        >
          More content available. Use start_index={result.next_start_index} to continue reading.
        </div>
      )}
    </div>
  );
};

// Fetch URL Content Renderer - For web content fetching and summarization
export const FetchUrlRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Fetching web content...
      </div>
    );
  }

  // Handle batch format: { fetched: [...] }
  const fetched: FetchResult[] = output.fetched || [output];

  if (!fetched.length) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Web content fetched
      </div>
    );
  }

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0 0 0;
      `}
    >
      {fetched.map((fetchResult, index) => (
        <SingleFetchResult key={index} result={fetchResult} showDivider={index > 0} />
      ))}
    </div>
  );
};
